import { APP_TERMS_VERSION as TERMS_VERSION, APP_TERMS_LAST_UPDATE as LAST_UPDATE } from "@/content/productLegal";
import { PetTerms, RecipeTerms, ProductPrivacy, ProductPrivacyConsent } from "@/content/productLegal";
import { PrivacyAnalyticsClause, PRIVACY_VERSION, PRIVACY_LAST_UPDATE } from "@/components/PrivacyAnalyticsClause";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTheme } from "@/hooks/useTheme";
import type { FontSizeClasses } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { ChevronDown } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";

const API_URL = import.meta.env.VITE_API_URL || "https://api.ondiet.app";

interface TermsModalProps {
  onAccept: () => void;
}

export const TermsModal = ({ onAccept }: TermsModalProps) => {
  const [accepted, setAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [privacyOpen, setPrivacyOpen] = useState(true);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const { fontSize } = useTheme();
  const { user, session } = useAuth();

  const areaDeLeitura = useRef<HTMLDivElement | null>(null);
  const fimDoTexto = useRef<HTMLDivElement | null>(null);

  // Lock body scroll while modal is open (prevents iOS scroll bleed-through)
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);

  /**
   * O portão do aceite: destrava quando o FIM DO TEXTO fica visível.
   *
   * ⚠️ Antes era só o `onScroll` abaixo, e ele tem um modo de falha que TRANCA
   * a pessoa para fora do app: **evento de scroll não existe quando não há
   * scroll.** Se o conteúdo couber na altura da área — as duas sanfonas
   * fechadas, tela grande, ou o texto encolhendo por qualquer motivo —, o
   * `onScroll` nunca dispara, `hasScrolledToEnd` nunca vira `true`, e o
   * checkbox fica desabilitado para sempre. Sem aceite não há entrada no app:
   * o `ProtectedRoute` não deixa passar.
   *
   * O `IntersectionObserver` troca a pergunta de *"houve rolagem?"* para *"o
   * fim está à vista?"*, que é a pergunta que o portão sempre quis fazer. As
   * duas respostas coincidem quando há rolagem, e só a segunda continua certa
   * quando não há.
   *
   * `rootMargin` de 40px preserva a folga que o `onScroll` já usava — ninguém
   * precisa raspar o último pixel.
   *
   * O `onScroll` FICA como rede: se um WebView antigo não tiver
   * `IntersectionObserver`, o comportamento volta a ser exatamente o de hoje,
   * em vez de nenhum.
   */
  useEffect(() => {
    const fim = fimDoTexto.current;
    const area = areaDeLeitura.current;
    if (!fim || !area || typeof IntersectionObserver === "undefined") return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) {
          setHasScrolledToEnd(true);
        }
      },
      { root: area, rootMargin: "40px" },
    );
    observador.observe(fim);
    return () => observador.disconnect();
  }, []);

  const handleAccept = async () => {
    setAccepting(true);

    try {
      if (!user || !session?.access_token) throw new Error("Usuário não autenticado");

      const res = await fetch(`${API_URL}/api/record-consent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          auth_id: user.id,
          terms_version: TERMS_VERSION,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      toast.success("Termos aceitos com sucesso!");
      onAccept();
    } catch (error) {
      console.error('Erro ao registrar consentimento:', error);
      toast.error("Erro ao aceitar termos. Tente novamente.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      style={{ touchAction: "none" }}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div
        className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col border border-border overflow-hidden"
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-border">
          <h2 className={`${fontSize.lg} font-semibold text-foreground`}>
            Termos de Uso e Política de Privacidade
          </h2>
          <p className={`${fontSize.sm} text-muted-foreground mt-1`}>
            Leia os documentos abaixo e role até o final para continuar
          </p>
        </div>

        <div
          ref={areaDeLeitura}
          data-testid="termos-area-de-leitura"
          className="flex-1 overflow-y-auto min-h-0"
          style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
          // Rede para WebView sem `IntersectionObserver`. Ver o efeito acima.
          onScroll={(e) => {
            const el = e.currentTarget;
            const threshold = 40;
            if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
              setHasScrolledToEnd(true);
            }
          }}
        >
          <div className={`p-5 ${fontSize.sm} text-muted-foreground leading-relaxed space-y-3`}>
            {/* Accordion: Termos de Uso */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setTermsOpen(!termsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 ${fontSize.sm} font-semibold text-foreground bg-muted/50 hover:bg-muted transition-colors`}
              >
                📄 Termos de Uso
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${termsOpen ? "rotate-180" : ""}`} />
              </button>
              {termsOpen && (
                <div className="px-4 py-4 border-t border-border">
                  <TermsContent fontSize={fontSize} />
                </div>
              )}
            </div>

            {/* Accordion: Política de Privacidade */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setPrivacyOpen(!privacyOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 ${fontSize.sm} font-semibold text-foreground bg-muted/50 hover:bg-muted transition-colors`}
              >
                🔒 Política de Privacidade
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${privacyOpen ? "rotate-180" : ""}`} />
              </button>
              {privacyOpen && (
                <div className="px-4 py-4 border-t border-border">
                  <PrivacyContent fontSize={fontSize} />
                </div>
              )}
            </div>

            {/* Sentinela do portão. Fica DEPOIS das duas sanfonas e dentro da
                área rolável — é a existência dela à vista que significa "leu
                até o fim". Sem altura própria: não desenha nada. */}
            <div ref={fimDoTexto} data-testid="termos-fim-do-texto" aria-hidden="true" />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border space-y-4">
          {!hasScrolledToEnd && (
            <p className={`${fontSize.xs} text-center text-muted-foreground`}>
              ↓ Role até o final para habilitar a aceitação
            </p>
          )}
          <label className={`flex items-start gap-3 ${hasScrolledToEnd ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => hasScrolledToEnd && setAccepted(v === true)}
              disabled={!hasScrolledToEnd}
              className="mt-0.5"
            />
            <span className={`${fontSize.sm} text-muted-foreground`}>
              Li e concordo com os <strong className="text-foreground">Termos de Uso</strong> e a{" "}
              <strong className="text-foreground">Política de Privacidade</strong>, autorizando o
              tratamento de meus dados conforme descrito.
            </span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!accepted || accepting}
            className={`w-full py-3.5 rounded-lg font-semibold transition-all ${fontSize.base} ${
              accepted && !accepting
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
          >
            {accepting ? "Registrando consentimento..." : "Aceitar e Continuar"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Termos de Uso (texto completo idêntico a Terms.tsx) ─── */

const TermsContent = ({ fontSize }: { fontSize: FontSizeClasses }) => (
  <div className="space-y-6">
    <p>
      Estes Termos de Uso regulam a utilização do aplicativo OnDiet ("Aplicativo"). Ao instalar,
      acessar ou utilizar o Aplicativo, você declara ter lido, compreendido e concordado
      integralmente com estes termos. Este documento está disponível permanentemente em
      ondiet.app/app-terms e na seção Documentos Legais do Aplicativo.
    </p>

    <div className="bg-accent/10 border border-border-strong/30 rounded-lg p-4">
      <p className={`text-muted-foreground dark:text-muted-foreground ${fontSize.sm}`}>
        <strong>IMPORTANTE:</strong> O Aplicativo é uma ferramenta de acompanhamento nutricional
        destinada exclusivamente a usuários com assinatura ativa do serviço OnDiet, contratado
        fora do Aplicativo. O Aplicativo não processa contratação nem pagamento. A gestão,
        alteração e confirmação do cancelamento acontecem no portal externo seguro do Stripe.
      </p>
    </div>

    {/* 1. IDENTIFICAÇÃO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>1. IDENTIFICAÇÃO</h3>
      <div className="space-y-1">
        <p><strong>Razão Social:</strong> ON SOLUÇÕES LTDA</p>
        <p><strong>CNPJ:</strong> 64.742.357/0001-28</p>
        <p><strong>Endereço:</strong> Rua Periquito, 225 – São Paulo, SP</p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
        <p><strong>WhatsApp:</strong> (11) 93920-4456</p>
        <p><strong>Site:</strong> www.ondiet.app</p>
      </div>
    </div>

    {/* 2. OBJETO E FINALIDADE */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>2. OBJETO E FINALIDADE DO APLICATIVO</h3>
      <p className="mb-3">
        Estes Termos regulam o uso do Aplicativo OnDiet, plataforma digital de acompanhamento
        nutricional que integra visualização de protocolos personalizados, coleta de dados de
        saúde via assistente de inteligência artificial, e suporte ao usuário ao longo do serviço.
      </p>
      <p className="mb-2">O Aplicativo é organizado em quatro áreas principais:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li><strong>L.IA</strong> – Assistente de suporte por chat para coleta de dados da anamnese, registro de feedbacks semanais e suporte a dúvidas sobre o protocolo nutricional.</li>
        <li><strong>PLANO</strong> – Visualização do protocolo nutricional semanal com cardápios por refeição, dados calóricos, funcionalidade de substituição de alimentos e ferramentas de acompanhamento (como registro de refeições e de hidratação, quando disponíveis).</li>
        <li><strong>PERFIL</strong> – Exibição de dados nutricionais do plano, configurações do usuário, detalhes da assinatura e acesso a documentos legais.</li>
        <li><strong>PET</strong> – Personagem de incentivo à constância.</li>
<li><strong>RECEITAS — QUANDO DISPONIBILIZADAS</strong> – Biblioteca, favoritas e substituição contextual de refeições.</li>
      </ul>
    </div>

    {/* 3. ELEGIBILIDADE E ACESSO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>3. ELEGIBILIDADE E ACESSO</h3>
      <p className="mb-3">
        O uso do Aplicativo é destinado exclusivamente a usuários com assinatura ativa do serviço
        OnDiet, previamente contratada pelos canais oficiais da OnDiet.
      </p>
      <p className="mb-2">Para acessar o Aplicativo, o usuário deve:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Possuir assinatura ativa do serviço OnDiet</li>
        <li>Ter 18 (dezoito) anos completos ou mais</li>
        <li>Utilizar as credenciais de acesso (e-mail e senha) fornecidas no momento da contratação</li>
      </ul>
      <p className="mt-3">
        O Aplicativo não permite novos cadastros, contratações ou pagamentos. O acesso é
        autenticado por e-mail e senha vinculados à assinatura existente.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm} mt-4`}>3.1. Consentimento no Primeiro Acesso</h4>
      <p>
        No primeiro acesso ao Aplicativo após instalação ou atualização, antes de qualquer acesso
        às funcionalidades ou coleta de dados, o usuário é apresentado a uma tela de consentimento
        dedicada. Nessa tela, o usuário deve aceitar expressamente estes Termos de Uso e a Política
        de Privacidade para prosseguir. Somente após a concessão desse consentimento o usuário
        poderá acessar o Aplicativo e iniciar a anamnese com a L.IA. O acesso sem consentimento
        não é permitido.
      </p>
    </div>

    {/* 4. FUNCIONALIDADES DO APLICATIVO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>4. FUNCIONALIDADES DO APLICATIVO</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>4.1. L.IA – Assistente de Suporte por Chat</h4>
      <p className="mb-2">
        A L.IA é um assistente de inteligência artificial desenvolvido para apoiar o usuário
        durante o uso do serviço OnDiet. Suas funções incluem:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Condução da anamnese inicial: coleta estruturada de dados de saúde, hábitos alimentares, composição corporal, objetivos e informações relevantes para a elaboração do protocolo nutricional pelo nutricionista responsável</li>
        <li>Coleta de feedbacks semanais: registro de peso atual, frequência de treinos, nível de adesão ao protocolo, refeições livres e observações gerais da semana</li>
        <li>Coleta de feedback mensal: avaliação mais ampla da evolução do usuário ao longo do mês, incluindo percepção de resultados, ajustes de rotina e novas informações de saúde relevantes para reavaliação do protocolo</li>
        <li>Suporte a dúvidas: respostas sobre o protocolo nutricional ativo, orientações sobre como seguir o plano e esclarecimentos gerais sobre alimentação</li>
      </ul>
      <p className="mb-4">
        <strong>AVISO:</strong> A L.IA é uma assistente de suporte. Não substitui orientação de nutricionista.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>4.2. PLANO – Protocolo Nutricional</h4>
      <p className="mb-2">
        A seção PLANO exibe o protocolo nutricional personalizado elaborado pelo nutricionista
        responsável. O usuário pode:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Visualizar o cardápio semanal organizado por refeições: Café da manhã, Almoço, Lanche da tarde, Jantar e Lanche Coringa</li>
        <li>Consultar as calorias por refeição e as opções de alimentos em cada categoria (A, B, C e subtipos)</li>
        <li>Realizar substituições de alimentos: clicar sobre qualquer alimento para substituí-lo por outra opção da mesma categoria dentro do banco pré-aprovado pelo nutricionista</li>
        <li>Acessar a tabela de doces permitidos, quando a opção de doces estiver ativa no plano</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>4.3. PERFIL – Dados e Configurações</h4>
      <p className="mb-2">A seção PERFIL oferece:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Dados do plano: Objetivo, Taxa Metabólica Basal (TMB), Meta calórica diária e Meta de ingestão de água</li>
        <li>Configurações do plano: alteração da frequência alimentar (4, 5 ou 6 refeições diárias) e ativação/desativação de doces na dieta</li>
        <li>Configurações do aplicativo: ajuste de tamanho de fonte (Pequeno ou Grande) e ativação do Modo Escuro</li>
        <li>Detalhes da assinatura: status do plano, tipo do plano e acesso ao portal de gerenciamento de assinatura via browser externo</li>
        <li>Instruções do Plano: guia de 5 passos sobre como seguir o protocolo nutricional corretamente</li>
        <li>Documentos Legais: acesso a estes Termos de Uso e à Política de Privacidade</li>
        <li>Trocar Senha: alteração da senha de acesso ao Aplicativo</li>
        <li>Ajuda: contato com a equipe OnDiet via e-mail e WhatsApp</li>
        <li>Sair da Conta: encerramento da sessão ativa</li>
        <li>Excluir Conta: solicitação de exclusão de dados e encerramento definitivo do acesso</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm} mt-4`}>4.4. PET – Incentivo à Constância</h4>
<PetTerms />
<h4 className={`font-medium text-foreground mb-2 ${fontSize.sm} mt-4`}>4.5. RECEITAS — Quando Disponibilizadas</h4>
<RecipeTerms />
    </div>

    {/* 5. NATUREZA DO CONTEÚDO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>5. NATUREZA DO CONTEÚDO E AVISOS IMPORTANTES</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>5.1. Caráter Informativo e Educacional</h4>
      <p className="mb-4">
        O conteúdo exibido no Aplicativo — protocolos nutricionais, cardápios, informações calóricas
        e orientações — tem caráter exclusivamente informativo e educacional, sendo elaborado por
        nutricionista registrado no CFN.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>5.2. Não Substitui Acompanhamento Médico</h4>
      <p className="mb-4">
        As informações e orientações do Aplicativo não substituem consulta médica, nutricional
        presencial ou qualquer outro tipo de acompanhamento clínico. O usuário deve consultar
        médico ou nutricionista sempre que necessário, especialmente diante de qualquer sintoma
        ou desconforto.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>5.3. A L.IA Não É Nutricionista</h4>
      <p className="mb-4">
        A L.IA é uma assistente de inteligência artificial de suporte. Não realiza diagnósticos,
        não prescreve medicamentos e não elabora protocolos nutricionais de forma autônoma.
        Toda decisão clínica e nutricional é de responsabilidade exclusiva do nutricionista habilitado.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>5.4. Responsabilidade Técnica do Nutricionista</h4>
      <p>
        O nutricionista Enzo Chen (CRN 67468/SP) é o responsável técnico por todos os protocolos
        nutricionais elaborados através da plataforma OnDiet. A equipe de nutricionistas da OnDiet
        é formada por profissionais contratados pela ON SOLUÇÕES LTDA, todos habilitados pelo CFN,
        atuando sob a supervisão e responsabilidade técnica do nutricionista responsável. Os dados
        coletados pela L.IA são utilizados exclusivamente para subsidiar a elaboração e os ajustes
        dos protocolos pelos profissionais habilitados.
      </p>
    </div>

    {/* 6. DADOS COLETADOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>6. DADOS COLETADOS PELO APLICATIVO</h3>
      <p className="mb-3">
        Ao utilizar o Aplicativo, o usuário reconhece que os seguintes dados são coletados e processados:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Dados de autenticação: e-mail e senha criptografada</li>
        <li>Dados de saúde coletados via L.IA: composição corporal, histórico de saúde, medicamentos em uso, hábitos alimentares, nível de atividade física, objetivos e demais informações da anamnese</li>
        <li>Feedbacks semanais e feedback mensal: peso atual, frequência de treinos, adesão ao protocolo, refeições livres, observações da semana e avaliação mensal de evolução e resultados</li>
        <li>Registro de refeições e de hidratação (opcionais), quando o usuário utiliza essas ferramentas de acompanhamento</li>
        <li>Preferências do plano: frequência alimentar escolhida e preferência por doces na dieta</li>
        <li>Preferências do aplicativo: tamanho de fonte e modo escuro</li>
        <li>Atividade, conquistas e preferências do pet; quando disponíveis, favoritas, escolhas de receitas por dia e refeição, quantidades adaptadas e consumo declarado</li>
        <li>Dados técnicos de sessão: identificador de sessão para manutenção do acesso autenticado</li>
      </ul>
      <p>
        O tratamento desses dados é regido pela Política de Privacidade do Aplicativo, disponível
        na seção Documentos Legais e em ondiet.app/app-privacy.
      </p>
    </div>

    {/* 7. RESPONSABILIDADES DO USUÁRIO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>7. RESPONSABILIDADES DO USUÁRIO</h3>
      <p className="mb-2">Ao utilizar o Aplicativo, o usuário se compromete a:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Manter suas credenciais de acesso em sigilo e não compartilhar o acesso com terceiros</li>
        <li>Fornecer informações verdadeiras e completas nas interações com a L.IA</li>
        <li>Utilizar o Aplicativo de forma responsável e legal</li>
        <li>Não tentar acessar áreas restritas ou dados de outros usuários</li>
        <li>Não reproduzir, copiar, distribuir ou publicar conteúdos do Aplicativo sem autorização</li>
        <li>Comunicar imediatamente qualquer alteração relevante de saúde que possa impactar a adequação do protocolo</li>
        <li>Consultar profissional de saúde presencialmente sempre que necessário</li>
        <li>Manter seu dispositivo e sistema operacional atualizados para correto funcionamento do Aplicativo</li>
      </ul>
    </div>

    {/* 8. LIMITAÇÕES DO APLICATIVO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>8. LIMITAÇÕES DO APLICATIVO</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>8.1. Funcionalidades Não Disponíveis</h4>
      <p className="mb-2">O usuário reconhece e aceita que:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>O Aplicativo NÃO permite contratação de planos</li>
        <li>O Aplicativo NÃO processa pagamentos</li>
        <li>O cancelamento e a alteração de assinaturas são confirmados no portal externo seguro do Stripe</li>
        <li>O Aplicativo NÃO realiza diagnósticos médicos nem prescreve medicamentos</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>8.2. Dependência de Conexão</h4>
      <p className="mb-4">
        O Aplicativo depende de conexão estável à internet para funcionar adequadamente.
        A OnDiet não se responsabiliza por problemas de conexão do usuário.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>8.3. Compatibilidade</h4>
      <p>
        O usuário deve garantir que seu dispositivo e sistema operacional sejam compatíveis com o Aplicativo.
        A OnDiet não se responsabiliza por incompatibilidades com dispositivos ou sistemas desatualizados.
      </p>
    </div>

    {/* 9. PROPRIEDADE INTELECTUAL */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>9. PROPRIEDADE INTELECTUAL</h3>
      <p className="mb-3">
        Todos os direitos de propriedade intelectual sobre o Aplicativo, incluindo a marca "OnDiet",
        logotipos, interface, códigos, metodologia nutricional, protocolos e demais conteúdos,
        pertencem exclusivamente à ON SOLUÇÕES LTDA.
      </p>
      <p className="mb-3">
        O usuário recebe licença limitada, não exclusiva e intransferível para utilizar o Aplicativo
        exclusivamente para acompanhamento de seu próprio plano alimentar.
      </p>
      <p className="mb-2">É expressamente proibido:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Reproduzir, copiar ou distribuir conteúdos do Aplicativo</li>
        <li>Realizar engenharia reversa do Aplicativo</li>
        <li>Utilizar a marca "OnDiet" sem autorização expressa</li>
        <li>Compartilhar protocolos nutricionais publicamente</li>
      </ul>
    </div>

    {/* 10. SUSPENSÃO OU ENCERRAMENTO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>10. SUSPENSÃO OU ENCERRAMENTO DE ACESSO</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>10.1. Término do Serviço</h4>
      <p className="mb-4">
        O acesso ao Aplicativo está diretamente vinculado à assinatura ativa do serviço OnDiet.
        Em caso de cancelamento, inadimplência ou término da assinatura, o acesso será
        automaticamente suspenso ou encerrado.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>10.2. Uso Indevido</h4>
      <p className="mb-4">
        A OnDiet reserva-se o direito de suspender ou encerrar o acesso ao Aplicativo em caso de:
        violação destes Termos de Uso, uso indevido ou fraudulento, compartilhamento não autorizado
        de credenciais ou tentativa de acesso a dados de terceiros.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>10.3. Exclusão de Conta</h4>
      <p className="mb-3">
              O usuário pode solicitar a exclusão de sua conta diretamente na seção PERFIL &gt; Minha
              conta &gt; Excluir conta, ou por e-mail em contato@ondiet.app. Quando a solicitação é aceita:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>O acesso ao Aplicativo é bloqueado quando a solicitação é aceita.</li>
              <li>A remoção de dados dos sistemas ativos segue processamento automático por categoria. Uma confirmação posterior informa o andamento da solicitação; o aceite, por si só, não confirma a conclusão.</li>
              <li>Registros clínicos, fiscais, de segurança ou auditoria podem ser conservados em acesso restrito quando houver finalidade e base aplicáveis. O escopo e o prazo dependem da categoria e das regras profissionais ou legais vigentes; não se aplica um prazo único a todos os dados.</li>
              <li>Fornecedores e cópias de backup seguem processos e ciclos próprios. O aceite da solicitação não significa que todos esses processos externos foram concluídos.</li>
              <li>A exclusão é irreversível, encerra o período de acesso e não gera reembolso, inclusive proporcional.</li>
              <li>Preferências locais armazenadas no dispositivo são removidas ao desinstalar o Aplicativo.</li>
            </ul>
    </div>

    {/* 11. ATUALIZAÇÕES */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>11. ATUALIZAÇÕES DO APLICATIVO</h3>
      <p className="mb-3">
        O Aplicativo pode ser atualizado periodicamente para melhorias de funcionalidade,
        segurança, correção de bugs ou adequação a novas tecnologias ou diretrizes das lojas
        de aplicativos.
      </p>
      <p>
        Recomenda-se que o usuário mantenha o Aplicativo sempre atualizado. A OnDiet não se
        responsabiliza por problemas decorrentes do uso de versões desatualizadas.
      </p>
    </div>

    {/* 12. LIMITAÇÃO DE RESPONSABILIDADE */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>12. LIMITAÇÃO DE RESPONSABILIDADE</h3>
      <p className="mb-2">A OnDiet não se responsabiliza por:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Falhas de conexão à internet do usuário</li>
        <li>Incompatibilidade com dispositivos ou sistemas operacionais desatualizados</li>
        <li>Decisões do usuário baseadas nas informações do Aplicativo sem consultar profissional de saúde</li>
        <li>Perda de dados decorrente de problemas no dispositivo do usuário</li>
        <li>Uso não autorizado de credenciais por terceiros</li>
        <li>Indisponibilidades temporárias para manutenção programada, mediante aviso prévio</li>
        <li>Restrições impostas pelas lojas de aplicativos (App Store, Google Play Store), incluindo remoção temporária ou definitiva do Aplicativo por decisão dessas plataformas</li>
      </ul>
    </div>

    {/* 13. ALTERAÇÕES DOS TERMOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>13. ALTERAÇÕES DOS TERMOS DE USO</h3>
      <p className="mb-3">
        Estes Termos de Uso podem ser atualizados periodicamente. Alterações substanciais serão
        comunicadas através dos canais oficiais da OnDiet (e-mail e/ou notificação no Aplicativo). A versão atualizada indicará sua data de entrada em vigor.
      </p>
      <p>
        O uso continuado do Aplicativo após as alterações caracteriza aceitação dos novos termos,
        exceto quando as alterações implicarem ampliação do escopo do tratamento de dados sensíveis
        de saúde — nesse caso, será solicitado novo consentimento explícito do usuário antes que
        o novo tratamento se inicie.
      </p>
    </div>

    {/* 14. LEGISLAÇÃO APLICÁVEL */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>14. LEGISLAÇÃO APLICÁVEL E FORO</h3>
      <p className="mb-2">
        Estes Termos de Uso são regidos pelas leis da República Federativa do Brasil, em especial:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Lei 13.709/2018 (Lei Geral de Proteção de Dados – LGPD)</li>
        <li>Lei 8.078/1990 (Código de Defesa do Consumidor)</li>
        <li>Lei 12.965/2014 (Marco Civil da Internet)</li>
      </ul>
      <p>
        Fica eleito o foro da Comarca de São Paulo/SP para dirimir quaisquer controvérsias,
        ressalvado o direito do consumidor de escolher o foro de seu domicílio conforme o
        Código de Defesa do Consumidor.
      </p>
    </div>

    {/* 15. CONTATO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>15. CONTATO</h3>
      <div className="space-y-1">
        <p><strong>ON SOLUÇÕES LTDA</strong></p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
        <p><strong>WhatsApp:</strong> (11) 93920-4456</p>
        <p><strong>Site:</strong> www.ondiet.app</p>
      </div>
    </div>

    <div className="bg-muted border border-border rounded-lg p-4 mt-4">
      <p className={`text-center ${fontSize.sm} text-muted-foreground`}>
        Ao utilizar o Aplicativo OnDiet, você declara ter lido e concordado com estes Termos de Uso.
      </p>
      <p className={`text-center ${fontSize.xs} text-muted-foreground mt-2`}>
        <strong>Versão:</strong> {TERMS_VERSION} |
        <strong> Última atualização:</strong> {LAST_UPDATE}
      </p>
    </div>
  </div>
);

/* ─── Política de Privacidade (texto completo idêntico a Privacy.tsx) ─── */

const PrivacyContent = ({ fontSize }: { fontSize: FontSizeClasses }) => (
  <div className="space-y-6">
    <PrivacyAnalyticsClause />
    <p>
      Esta Política de Privacidade descreve como o aplicativo OnDiet ("Aplicativo") coleta, utiliza,
      armazena e protege os dados pessoais do usuário, em conformidade com a Lei Geral de Proteção
      de Dados (Lei 13.709/2018 – LGPD) e demais legislações aplicáveis. Este documento está
      disponível permanentemente em ondiet.app/app-privacy e na seção Documentos Legais do Aplicativo.
    </p>

    <div className="bg-accent/10 border border-border-strong/30 rounded-lg p-4">
      <p className={`text-muted-foreground dark:text-muted-foreground ${fontSize.sm}`}>
        <strong>IMPORTANTE:</strong> O Aplicativo coleta dados de saúde do usuário por meio da
        assistente L.IA para fins exclusivos de personalização e acompanhamento nutricional
        profissional. Esses dados nunca são utilizados para fins publicitários nem compartilhados
        com terceiros para fins de marketing.
      </p>
    </div>

    {/* 1. IDENTIFICAÇÃO DO CONTROLADOR */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>1. IDENTIFICAÇÃO DO CONTROLADOR DE DADOS</h3>
      <div className="space-y-1">
        <p><strong>Razão Social:</strong> ON SOLUÇÕES LTDA</p>
        <p><strong>CNPJ:</strong> 64.742.357/0001-28</p>
        <p><strong>Endereço:</strong> Rua Periquito, 225 – São Paulo, SP</p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
        <p><strong>Site:</strong> www.ondiet.app</p>
      </div>
      <div className="mt-3 space-y-1">
        <p><strong>Encarregado de Dados (DPO):</strong></p>
        <p><strong>Nome:</strong> Thiago Seraphim</p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
      </div>
    </div>

    {/* 2. FINALIDADE DO APLICATIVO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>2. FINALIDADE DO APLICATIVO</h3>
      <p className="mb-2">
        O Aplicativo OnDiet é uma plataforma digital de acompanhamento nutricional que permite ao usuário:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Visualizar e acompanhar seu protocolo nutricional semanal personalizado</li>
        <li>Interagir com a assistente de IA L.IA para fornecer dados da anamnese inicial e feedbacks semanais</li>
        <li>Realizar substituições de alimentos dentro do banco pré-aprovado pelo nutricionista</li>
        <li>Gerenciar configurações de plano e preferências do aplicativo</li>
      </ul>
      <p>
        As informações necessárias ao acompanhamento viabilizam a prestação do serviço
        de acompanhamento nutricional pela OnDiet, sob supervisão do nutricionista responsável
        Enzo Chen (CRN 67468/SP).
      </p>
    </div>

    {/* 3. DADOS COLETADOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>3. DADOS COLETADOS PELO APLICATIVO</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.1. Dados de Autenticação e Acesso</h4>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>E-mail de acesso</li>
        <li>Senha criptografada (armazenada em formato hash, nunca em texto simples)</li>
        <li>Identificador de sessão (para manter o usuário autenticado)</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.2. Dados de Saúde Coletados via L.IA (Dados Sensíveis)</h4>
      <p className="mb-2">
        A L.IA conduz a anamnese inicial e os feedbacks semanais por meio de conversa estruturada
        no chat. Os dados coletados nesse processo incluem:
      </p>

      <p className="font-medium mb-2 mt-3">Anamnese inicial:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Nome completo</li>
        <li>Data de nascimento e idade</li>
        <li>Sexo biológico</li>
        <li>Peso e altura atuais</li>
        <li>Medidas corporais (quando fornecidas)</li>
        <li>Objetivo nutricional (emagrecimento, hipertrofia, manutenção ou performance)</li>
        <li>Histórico de saúde e condições clínicas preexistentes</li>
        <li>Medicamentos e suplementos em uso</li>
        <li>Alergias e intolerâncias alimentares</li>
        <li>Preferências e restrições alimentares</li>
        <li>Nível e frequência de atividade física</li>
        <li>Rotina de trabalho e horários habituais de refeições</li>
        <li>Hábitos de consumo de álcool e tabaco</li>
        <li>Qualidade do sono e nível de estresse percebido</li>
      </ul>

      <p className="font-medium mb-2">Feedbacks semanais:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Peso atual na semana</li>
        <li>Frequência de treinos na semana</li>
        <li>Nível de adesão ao protocolo nutricional</li>
        <li>Quantidade de refeições livres realizadas</li>
        <li>Observações e intercorrências da semana</li>
      </ul>

      <p className="font-medium mb-2">Feedback mensal:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Avaliação de evolução e percepção de resultados ao longo do mês</li>
        <li>Ajustes de rotina, condição física ou objetivos identificados no período</li>
        <li>Novas informações de saúde relevantes para reavaliação do protocolo nutricional</li>
      </ul>

      <p className="font-medium mb-2">Registro de acompanhamento:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Registro de refeições (feita, parcial ou pulada) e de hidratação/consumo de água, quando o usuário opta por utilizar essas funcionalidades de acompanhamento</li>
      </ul>

      <p className="font-medium mb-2">Mensagens do chat:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Histórico de conversas com a L.IA, armazenado para fins de continuidade do atendimento e subsídio ao nutricionista responsável</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.3. Preferências e Configurações do Plano</h4>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Frequência alimentar escolhida (4, 5 ou 6 refeições diárias)</li>
        <li>Preferência por doces na dieta (ativado/desativado)</li>
        <li>Substituições de alimentos realizadas dentro do app</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.4. Preferências do Aplicativo (Dados Locais)</h4>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
        <li>Tamanho de fonte preferido (Pequeno ou Grande)</li>
        <li>Preferência por Modo Escuro</li>
              <li>Preferência opcional de métricas de uso, separada por conta neste dispositivo</li>
      </ul>
      <p className="mb-4">
        Estas preferências são armazenadas localmente no dispositivo do usuário e não são
        transmitidas aos servidores da OnDiet.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.5. Dados Técnicos de Sessão</h4>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Tipo de dispositivo e sistema operacional (para diagnóstico de compatibilidade)</li>
        <li>Versão do Aplicativo instalada</li>
        <li>Horários de acesso e interação (logs de sessão)</li>
        <li>Data e horário de abertura do Aplicativo (para acompanhamento clínico do paciente pelo nutricionista responsável e identificação de períodos de inatividade prolongada)</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.6. Uso do Microfone</h4>
      <p className="mb-4">
        O Aplicativo acessa o microfone do dispositivo exclusivamente quando o usuário inicia, de forma
        voluntária, o envio de uma mensagem de áudio na L.IA. O acesso ocorre apenas durante a gravação
        e é encerrado imediatamente após. Os arquivos de áudio são transmitidos para processamento e não
        são armazenados localmente no dispositivo. O Aplicativo NÃO acessa o microfone em segundo plano
        nem fora da interação de gravação iniciada pelo usuário.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.7. Uso da Câmera e da Galeria de Fotos</h4>
<p className="mb-3">Acesso apenas quando o usuário escolhe definir ou alterar o avatar da conta, sem acesso em segundo plano ou importação automática. O avatar não é exibido a outros usuários por esse recurso.</p>


      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.8. O Que o Aplicativo NÃO Coleta</h4>
      <p className="mb-2">O Aplicativo NÃO coleta nem acessa:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Dados de localização GPS</li>
        <li>Lista de contatos do dispositivo</li>
        <li>Dados para fins publicitários ou rastreamento publicitário; as métricas opcionais de uso seguem o consentimento específico descrito nesta política</li>
        <li>Dados financeiros ou bancários (pagamentos ocorrem fora do Aplicativo)</li>
        <li>Dados de saúde da plataforma HealthKit (Apple) ou Google Fit</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>3.9. Pet e Receitas</h4>
<ProductPrivacy />

    </div>

    {/* 4. BASE LEGAL */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>4. BASE LEGAL PARA O TRATAMENTO DE DADOS</h3>
      <p className="mb-3">
        O tratamento dos dados coletados pelo Aplicativo é fundamentado nas seguintes bases legais da LGPD:
      </p>
      <div className="overflow-x-auto">
        <table className={`w-full ${fontSize.sm} border border-border`}>
          <thead>
            <tr className="bg-muted">
              <th className="border border-border p-2 text-left font-semibold text-foreground">Categoria de Dado</th>
              <th className="border border-border p-2 text-left font-semibold text-foreground">Finalidade</th>
              <th className="border border-border p-2 text-left font-semibold text-foreground">Base Legal (LGPD)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-border p-2">Autenticação (e-mail, senha)</td>
              <td className="border border-border p-2">Controle de acesso ao Aplicativo</td>
              <td className="border border-border p-2">Execução de contrato – Art. 7º, V</td>
            </tr>
            <tr>
              <td className="border border-border p-2">Dados de saúde – Anamnese</td>
              <td className="border border-border p-2">Elaboração do protocolo nutricional</td>
              <td className="border border-border p-2">Consentimento explícito – Art. 11, I</td>
            </tr>
            <tr>
              <td className="border border-border p-2">Feedbacks, registro de refeições e hidratação</td>
              <td className="border border-border p-2">Ajuste do protocolo pelo nutricionista</td>
              <td className="border border-border p-2">Consentimento explícito – Art. 11, I</td>
            </tr>
            <tr>
              <td className="border border-border p-2">Histórico do chat com L.IA</td>
              <td className="border border-border p-2">Continuidade do acompanhamento</td>
              <td className="border border-border p-2">Consentimento explícito – Art. 11, I</td>
            </tr>
            <tr>
              <td className="border border-border p-2">Preferências do plano</td>
              <td className="border border-border p-2">Personalização da exibição</td>
              <td className="border border-border p-2">Execução de contrato – Art. 7º, V</td>
            </tr>
            <tr>
              <td className="border border-border p-2">Dados técnicos de sessão</td>
              <td className="border border-border p-2">Segurança e diagnóstico técnico</td>
              <td className="border border-border p-2">Legítimo interesse – Art. 7º, IX</td>
            </tr>
            <tr><td className="border border-border p-2">Pet e favoritas</td><td className="border border-border p-2">Experiência e preferências solicitadas</td><td className="border border-border p-2">Execução do contrato (Art. 7º, V)</td></tr><tr><td className="border border-border p-2">Receitas e consumo relacionados à saúde</td><td className="border border-border p-2">Adaptação e acompanhamento nutricional, quando disponíveis</td><td className="border border-border p-2">Consentimento explícito de saúde (Art. 11, I)</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    {/* 5. COMO UTILIZAMOS OS DADOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>5. COMO UTILIZAMOS OS DADOS</h3>
      <p className="mb-2">Os dados coletados pelo Aplicativo são utilizados para as seguintes finalidades:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Autenticar o acesso do usuário ao Aplicativo</li>
        <li>Exibir o protocolo nutricional personalizado do usuário</li>
        <li>Processar os dados da anamnese para subsidiar a elaboração do protocolo pelo nutricionista responsável</li>
        <li>Registrar e transmitir os feedbacks semanais ao sistema da OnDiet para viabilizar os ajustes semanais do protocolo</li>
        <li>Registrar refeições e hidratação informados pelo usuário, quando este opta por utilizar essas funcionalidades de acompanhamento</li>
        <li>Manter o histórico de conversa com a L.IA para continuidade do acompanhamento</li>
        <li>Salvar as preferências de configuração do plano e do aplicativo</li>
        <li>Garantir a segurança e o funcionamento técnico do Aplicativo</li>
        <li>Processar as mensagens e o contexto nutricional necessário por meio da OpenAI para responder no chat com a L.IA, conforme o consentimento específico do Aplicativo</li>
      <li>Melhorar telas e funcionalidades com métricas opcionais de uso, somente mediante consentimento específico e sem envio de dados de saúde ao Google Analytics</li>
            </ul>
      <p>
        Os dados de saúde coletados NUNCA são utilizados para fins publicitários, de rastreamento,
        de pesquisa de mercado ou compartilhados com terceiros para qualquer finalidade não
        relacionada diretamente à prestação do serviço de acompanhamento nutricional.
      </p>
    </div>

    {/* 6. COMPARTILHAMENTO DE DADOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>6. COMPARTILHAMENTO DE DADOS</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>6.1. Com Quem os Dados São Compartilhados</h4>
      <p className="mb-2">Os dados do usuário podem ser compartilhados exclusivamente com:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li><strong>Equipe de nutricionistas da OnDiet:</strong> profissionais contratados pela ON SOLUÇÕES LTDA, todos registrados no CFN, com acesso restrito aos dados dos usuários sob seu acompanhamento. A equipe atua sob supervisão e responsabilidade técnica do nutricionista responsável Enzo Chen (CRN 67468/SP), em conformidade com as normas do Conselho Federal de Nutricionistas</li>
        <li><strong>Supabase (banco de dados, autenticação e armazenamento de imagens):</strong> armazenamento seguro dos dados do usuário e dos arquivos do serviço, incluindo o avatar da conta nos servidores da OnDiet, hospedados via Supabase Inc. Os dados são processados em conformidade com os termos de processamento de dados da Supabase e sob instrução exclusiva da OnDiet</li>
        <li><strong>OpenAI (inteligência artificial):</strong> a tecnologia OpenAI é utilizada para o funcionamento do chat com a L.IA — recebe suas mensagens e os dados do seu protocolo nutricional necessários para responder (objetivo, metas de calorias e proteína, cardápio vigente, restrições alimentares e refeições registradas). O processamento ocorre em servidores localizados nos Estados Unidos. A OpenAI não utiliza esses dados para treinamento de modelos, publicidade ou quaisquer outros fins</li>
        <li><strong>Google Analytics (métricas opcionais de uso):</strong> recebe eventos de interação, códigos de etapa, identificadores de navegador e dados técnicos de conexão apenas após consentimento específico, para melhorar o aplicativo; sem envio de valores de peso, meta, IMC, respostas pessoais, nome, e-mail ou identificador da conta. Esta finalidade é independente do processamento de IA e não habilita publicidade personalizada.</li>
        <li><strong>OneSignal (notificações push):</strong> utilizado para envio de notificações no Aplicativo. O OneSignal recebe o device token do dispositivo do usuário exclusivamente para essa finalidade, sem acesso a dados de saúde ou de protocolo nutricional</li>
        <li><strong>Autoridades competentes:</strong> em caso de obrigação legal, judicial ou regulatória, nos termos da legislação brasileira</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>6.2. O Que Não Ocorre</h4>
      <p className="mb-2">A OnDiet NÃO:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Vende dados pessoais a terceiros</li>
        <li>Compartilha dados para finalidades publicitárias ou de marketing</li>
        <li>Utiliza dados de saúde para fins de rastreamento publicitário</li>
        <li>Permite acesso de terceiros aos dados sem base legal ou contratual</li>
      </ul>
    </div>

    {/* 7. SEGURANÇA E ARMAZENAMENTO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>7. SEGURANÇA E ARMAZENAMENTO</h3>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>7.1. Medidas de Segurança Implementadas</h4>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Comunicação criptografada via HTTPS/TLS entre o Aplicativo e os servidores da OnDiet</li>
        <li>Armazenamento de senhas exclusivamente em formato hash (nunca em texto simples)</li>
        <li>Controle de acesso por autenticação com sessões com expiração automática</li>
        <li>Armazenamento de dados em servidores com controles de acesso restrito</li>
        <li>Logs de auditoria para monitoramento de acesso a dados sensíveis</li>
        <li>Registro de endereço IP no momento do aceite de termos e consentimentos (para fins de auditoria e conformidade legal)</li>
      </ul>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>7.2. Armazenamento nos Servidores OnDiet</h4>
      <p className="mb-4">
        Os dados de saúde coletados via L.IA, o histórico de protocolo nutricional, os feedbacks
        e os dados de autenticação são armazenados nos servidores da OnDiet de forma segura e criptografada.
      </p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>7.3. Armazenamento Local no Dispositivo</h4>
      <p>
        As preferências de aparência (tamanho de fonte e modo escuro) e de métricas opcionais são
        armazenadas localmente no dispositivo do usuário. A preferência de métricas usa uma chave pseudônima derivada da conta para separar escolhas neste dispositivo. Após consentimento, cookies de analytics armazenam identificadores técnicos do navegador; esses identificadores não são anônimos. Esta integração de métricas não armazena localmente peso, meta, IMC ou respostas pessoais.
      </p>
    </div>

    {/* 8. RETENÇÃO DE DADOS */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>8. RETENÇÃO DE DADOS</h3>
      <p className="mb-2">Os dados do usuário são retidos pelos seguintes períodos:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li><strong>Dados de autenticação:</strong> eliminados ao concluir a exclusão da conta, ressalvados registros mínimos de segurança e auditoria sujeitos a obrigação legal.</li>
        <li><strong>Prontuários e registros clínicos:</strong> mantidos em arquivo restrito por pelo menos 20 (vinte) anos após o último registro, conforme a Resolução CFN nº 594/2017 e para eventual defesa em litígio. Esses registros deixam os sistemas ativos e não são usados para marketing ou personalização.</li>
        <li><strong>Histórico do chat com a L.IA:</strong> as mensagens trocadas com a assistente são apagadas em definitivo 90 (noventa) dias após o envio. Enquanto a conta existir, permanecem apenas um resumo da conversa e anotações de preferência que a L.IA usa para lembrar do usuário — como rotina de treino ou alimentos de que não gosta —, nunca o texto original das mensagens. O resumo e as anotações também são eliminados quando o usuário solicita a exclusão da conta.</li>
        <li><strong>Pet, preferências e favoritas:</strong> enquanto necessários à experiência da conta; na exclusão, seguem o processamento por categoria. Escolhas e consumo que integrem o acompanhamento clínico seguem a retenção aplicável, sem prazo adicional criado por pet ou receitas.</li>
        <li><strong>Logs técnicos de sessão:</strong> por até 12 (doze) meses.</li>
        <li><strong>Preferências locais de aparência e métricas opcionais:</strong> armazenadas no dispositivo até que o usuário as altere ou limpe o armazenamento do aplicativo/navegador.</li>
      </ul>
      <p className="mb-4">Após os prazos acima, os dados são eliminados de forma segura ou anonimizados de modo que a identificação não possa ser razoavelmente revertida. Estatísticas assim anonimizadas podem ser conservadas para análise e melhoria do serviço.</p>

      <h4 className={`font-medium text-foreground mb-2 ${fontSize.sm}`}>8.1. Exclusão de Conta e Dados</h4>
      <p className="mb-3">
              O usuário pode solicitar a exclusão de sua conta diretamente na seção PERFIL &gt; Minha
              conta &gt; Excluir conta, ou por e-mail em contato@ondiet.app. Quando a solicitação é aceita:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>O acesso ao Aplicativo é bloqueado quando a solicitação é aceita.</li>
              <li>A remoção de dados dos sistemas ativos segue processamento automático por categoria. Uma confirmação posterior informa o andamento da solicitação; o aceite, por si só, não confirma a conclusão.</li>
              <li>Registros clínicos, fiscais, de segurança ou auditoria podem ser conservados em acesso restrito quando houver finalidade e base aplicáveis. O escopo e o prazo dependem da categoria e das regras profissionais ou legais vigentes; não se aplica um prazo único a todos os dados.</li>
              <li>Fornecedores e cópias de backup seguem processos e ciclos próprios. O aceite da solicitação não significa que todos esses processos externos foram concluídos.</li>
              <li>A exclusão é irreversível, encerra o período de acesso e não gera reembolso, inclusive proporcional.</li>
              <li>Preferências locais armazenadas no dispositivo são removidas ao desinstalar o Aplicativo.</li>
            </ul>
    </div>

    {/* 9. DIREITOS DO USUÁRIO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>9. DIREITOS DO USUÁRIO (LGPD)</h3>
      <p className="mb-2">
        Conforme a Lei Geral de Proteção de Dados (Lei 13.709/2018), o usuário possui os seguintes direitos:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Confirmação da existência de tratamento de seus dados</li>
        <li>Acesso aos dados pessoais tratados pelo Aplicativo</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados</li>
        <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade com a LGPD</li>
        <li>Portabilidade dos dados para outro fornecedor de serviço</li>
        <li>Eliminação dos dados tratados com base em consentimento</li>
        <li>Revogação do consentimento para tratamento de dados sensíveis de saúde</li>
        <li>Solicitação de exclusão de conta e dados diretamente no Aplicativo (seção Perfil &gt; Detalhes da assinatura &gt; Excluir conta)</li>
      </ul>
      <p className="mb-2">Para exercer seus direitos:</p>
      <div className="space-y-1">
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
        <p><strong>Prazo de resposta:</strong> até 15 (quinze) dias úteis</p>
      </div>
      <p className="mt-3">
        A revogação do consentimento para tratamento de dados de saúde pode inviabilizar a continuidade
        do serviço de acompanhamento nutricional, dado que esses dados são essenciais para a personalização
        do protocolo.
      </p>
    </div>

    {/* 10. CONSENTIMENTO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>10. CONSENTIMENTO PARA DADOS SENSÍVEIS DE SAÚDE</h3>
      <p className="mb-3">
        Por força do Art. 11 da LGPD, o tratamento de dados sensíveis de saúde exige consentimento
        específico e destacado do titular. Ao utilizar o Aplicativo e fornecer dados de saúde à L.IA,
        o usuário:
      </p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-3">
        <li>Consente expressamente com a coleta e o tratamento de seus dados de saúde para os fins descritos nesta Política</li>
        <li>Autoriza a transmissão desses dados ao nutricionista responsável para elaboração e ajuste do protocolo nutricional</li>
        <li>Declara estar ciente de que pode revogar esse consentimento a qualquer momento, ciente das implicações para a continuidade do serviço</li>
      </ul>
      <p className="mb-3">
        O consentimento é obtido no primeiro acesso ao Aplicativo após instalação ou atualização,
        por meio de tela dedicada apresentada antes de qualquer acesso às funcionalidades ou coleta
        de dados. O usuário deve aceitar expressamente esta Política de Privacidade e os Termos de
        Uso para prosseguir. Somente após esse consentimento o acesso ao Aplicativo e à anamnese é liberado.
      </p>
      <ProductPrivacyConsent />
    </div>

    {/* 11. ALTERAÇÕES DA POLÍTICA */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>11. ALTERAÇÕES DA POLÍTICA DE PRIVACIDADE</h3>
      <p className="mb-3">
        Esta Política de Privacidade pode ser atualizada periodicamente para refletir melhorias
        no Aplicativo, mudanças na legislação ou adequações às diretrizes das lojas de aplicativos.
        Alterações substanciais serão comunicadas por e-mail e/ou notificação no Aplicativo. A versão atualizada indicará sua data de entrada em vigor. Alterações que impliquem ampliação do escopo do
        tratamento de dados sensíveis de saúde exigirão novo consentimento explícito do usuário
        antes que o novo tratamento se inicie.
      </p>
      <p>
        A versão atualizada ficará sempre disponível em ondiet.app/app-privacy e na seção
        Documentos Legais do Aplicativo.
      </p>
    </div>

    {/* 12. CONTATO */}
    <div>
      <h3 className={`font-semibold text-foreground mb-3 ${fontSize.base}`}>12. CONTATO</h3>
      <div className="space-y-1">
        <p><strong>ON SOLUÇÕES LTDA</strong></p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
        <p><strong>WhatsApp:</strong> (11) 93920-4456</p>
        <p><strong>Site:</strong> www.ondiet.app</p>
      </div>
      <div className="mt-4 space-y-1">
        <p><strong>Encarregado de Dados (DPO):</strong></p>
        <p><strong>Nome:</strong> Thiago Seraphim</p>
        <p><strong>E-mail:</strong> contato@ondiet.app</p>
      </div>
    </div>

    <div className="bg-muted border border-border rounded-lg p-4 mt-4">
      <p className={`text-center ${fontSize.sm} text-muted-foreground`}>
        Esta Política de Privacidade está em conformidade com a Lei Geral de Proteção de Dados
        (Lei 13.709/2018) e demais legislações aplicáveis.
      </p>
      <p className={`text-center ${fontSize.xs} text-muted-foreground mt-2`}>
        <strong>Versão:</strong> {PRIVACY_VERSION} |
        <strong> Última atualização:</strong> {PRIVACY_LAST_UPDATE}
      </p>
    </div>
  </div>
);

export default TermsModal;
